import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { UUID } from 'angular2-uuid';
import { BdbSessionStrgService } from '@npm-bbta/bbog-cm-auth-lib';
import { AuthenticationRnecService } from './services/authentication-rnec.service';
import { FingerprintLocalRequest } from '../models/fingerprint-local-request.model';
import { GoogleTagManagerService } from '../shared/google-tag-manager.service';
import { RedirectService } from '../shared/redirect.service';
import { environment } from '../../../environments/environment';
import { FingerModel } from './finger.model';
import { RNECEventEnum } from './authentication-rnec.enum';
import { AdviserService } from '../shared/adviser.service';
import { OneTimePasswordService } from '../bdb-one-time-password/services/bdb-otp.service';
import { SendOtpHttpStatusEnum } from '../bdb-one-time-password/validate-otp/send-otp-http-status.enum';
import { ServiceErrorEnum } from '../enums/service-error.enum';
import { SessionStorageKeysEnum } from '../models/session-storage-keys-enum';
import { CustomerStatesEnum } from '../shared/models/enum/CustomerStatesEnum';
import { MediumTypeEnum } from '../enums/medium-type.enum';
import { ProductsEnum } from '../enums/products-enum';
import { FingerprintUtil } from '../shared/util/FingerprintUtil';
import { ErrorCode } from '../models/error-codes-v2';
import { BdbErrorV2ModalComponent } from '../bdb-error-v2-modal/bdb-error-v2-modal.component';
import { CustomLogger } from '@npm-bbta/bbog-sg-frontend-logs-lib';

type MessageType = 'SMS' | 'Whatsapp';

const logger = new CustomLogger('app-authentication-rnec', 'green')

@Component({
    selector: 'app-authentication-rnec',
    templateUrl: './authentication-rnec.component.html',
    styleUrls: ['./authentication-rnec.component.scss']
})
export class AuthenticationRnecComponent implements OnInit {

    @ViewChild('loader') loaderBdb: ElementRef;
    @ViewChild('modal') modalBdb: ElementRef;
    @ViewChild(BdbErrorV2ModalComponent) errorModal: BdbErrorV2ModalComponent;
    modalFunctionYes: Function;

    step = 1;
    intent = 1;
    closeResult: string;
    finger1: FingerModel = null;
    finger2: FingerModel = null;
    hardware = '';
    errorType = 0;

    @Input() fingerNameClass = '';
    @Input() fingerName = '';
    @Input() progressClass = '';
    @Input() buttonText = 'Comenzar ›';
    @Input() capturing = false;
    @Input() successFingerprint = false;
    @Input() successAuth = false;

    private readonly EXPDATE_MAX_TRIES = environment.expDateMaxAttempts;

    private readonly mediumTypesOTP: string[];
    private readonly mediumTypesRecognition: string[];

    constructor(
        private readonly oneTimePasswordService: OneTimePasswordService,
        private readonly authService: AuthenticationRnecService,
        private readonly googleTagManagerService: GoogleTagManagerService,
        private readonly redirectService: RedirectService,
        private readonly bdbSessionStrgService: BdbSessionStrgService,
        private readonly router: Router,
        private readonly adviserService: AdviserService,
        private readonly fingerprintUtil: FingerprintUtil
    ) {
        this.mediumTypesOTP = environment.mediumTypesOTP.split(',');
        this.mediumTypesRecognition = this.bdbSessionStrgService.get(SessionStorageKeysEnum.RECOGNITION_MEDIUM_TYPES) ?
            this.bdbSessionStrgService.get(SessionStorageKeysEnum.RECOGNITION_MEDIUM_TYPES).split(',') : [];
    }

    ngOnInit() {
        logger.log('Start RNEC Component Old')

        if (this.bdbSessionStrgService.get(SessionStorageKeysEnum.IS_NEW_AGENT_VERSION) === 'true') {
            this.router.navigate(['rnec-v2']);
        }

        this.hardware = this.bdbSessionStrgService.get(SessionStorageKeysEnum.HARDWARE);
        const { fingerLeftHand, fingerRightHand } = this.fingerprintUtil.initializeFingerToCapture();
        this.finger1 = fingerLeftHand;
        this.finger2 = fingerRightHand;
        window.scrollTo(0, 0);
        this.googleTagManagerService.sendInfo(window.location.pathname, 'Autenticación RNEC');
    }

    getGeolocationValues() {
        if (!this.hardware) {
            this.redirectService.redirectToError(ErrorCode.HARDWARE_NOT_FOUND, 'v2');
            return;
        }

        if (this.hardware !== 'old') {
            logger.log('Los dispositivos P80 no necesitan acceder a `navigator.geolocation`');
            return;
        }

        if (!navigator.geolocation) {
            logger.warn('Geolocation is not supported by this browser.');
            return;
        }

        const persistCoordinates: PositionCallback = (position) => {
            const latitude = String(position.coords.latitude);
            const longitude = String(position.coords.longitude);
            this.bdbSessionStrgService.save(SessionStorageKeysEnum.LATITUDE, latitude);
            this.bdbSessionStrgService.save(SessionStorageKeysEnum.LONGITUDE, longitude);
        }

        const handlePositionError: PositionErrorCallback = (error) => {
            logger.error('Geolocation error', error);

            if (environment.enableGeolocationModal) {
                this.redirectService.redirectToError(ErrorCode.LOCATION_ERROR, 'v2');
            }
        }

        const positionOptions: PositionOptions = {
            enableHighAccuracy: true,
            maximumAge: 0
        }

        navigator.geolocation.getCurrentPosition(persistCoordinates, handlePositionError, positionOptions);
    }

    async capture() {
        this.successFingerprint = false;
        switch (this.step) {
            case 1:
                await this.captureFirstHand();
                break;
            case 2:
                this.optionQuit();
                break;
            case 3:
                await this.captureSecondHand();
                break;
            case 4:
                this.successAuth = true;
                await this.validateFingers();
                break;
        }
    }

    async captureFirstHand() {
        logger.log('capturing first hand old...');
        this.renderFingerprintCapture(this.finger1, 'progress-first');
        if (this.hardware === 'new') {
            await this.captureFingerprintsWithLocalService('1');
        } else if (this.hardware === 'old') {
            await this.captureFingerPrintWithRnec(this.finger1.id, 'LEFT');
        }
    }

    async captureSecondHand() {
        logger.log('capturing second hand old...');
        this.renderFingerprintCapture(this.finger2, 'progress-second');
        if (this.hardware === 'new') {
            await this.captureFingerprintsWithLocalService('2');
        } else if (this.hardware === 'old') {
            await this.captureFingerPrintWithRnec(this.finger2.id, 'RIGHT');
        }
    }

    public async validateFingers(): Promise<void> {
        if (this.hardware === 'new') {
            await this.validateFingerprintWithLocalService();
        } else if (this.hardware === 'old') {
            await this.validateFingerprintWithRNEC();
        }
    }

    private async captureFingerprintsWithLocalService(handIndex: string): Promise<void> {
        logger.log(`capturing: ${handIndex}, current step: ${this.step}`);
        const finger = handIndex === '1' ? this.finger1.id : this.finger2.id;
        const numeroDocumento = Number(this.bdbSessionStrgService.get(SessionStorageKeysEnum.IDENTITY_NUMBER));
        const request: FingerprintLocalRequest = {
            SerialDispositivo: '1530I173349',
            NumeroDedo: finger,
            numeroDocumento,
            login: 'olimpia@bancodebogota.com.co',
            TipoDedo: handIndex
        };

        try {
            const response = await this.authService.captureFingerprintWithLocalService(request);

            if (response.successfulCapture) {
                await this.reportAuthenticationRNEC(RNECEventEnum.RNEC_CAPTURE, RNECEventEnum.OK, handIndex, finger, response);
                this.intent = 1;

                if (handIndex === '1') {
                    this.setNextFingerprintLayout();
                } else {
                    this.setValidationFingerprintLayout();
                }
            } else {
                await this.reportAuthenticationRNEC(RNECEventEnum.RNEC_CAPTURE, RNECEventEnum.ERROR, null, null, response);
                this.setStepAndHandleError(response.errorCode);
            }
        } catch (error) {
            console.error(error.isErrorAgent, error)
            if (error.isErrorAgent) {
                if (error.isErrorOnOff) {
                    this.bdbSessionStrgService.save('captorStatus', 'off');
                    this.capturing = false;
                    this.fingerNameClass = '';
                    this.setErrorAgentModalInfo();
                } else {
                    this.setErrorAgenteModalInfo()
                }
            } else {
                this.setStepAndHandleError();
            }
        }
    }

    private async validateFingerprintWithLocalService(): Promise<void> {
        const sellerDocumentNumber = this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELLER_IDENTITY_NUMBER);
        const sellerIdentityNumberLdap = this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELLER_IDENTITY_NUMBER_LDAP);
        const mediumType = this.bdbSessionStrgService.get(SessionStorageKeysEnum.MEDIUM_TYPE);
        const isSelfManagement = this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELF_MANAGEMENT) + '' === 'true';
        const isMediumTypeForRF = this.mediumTypesRecognition.includes(mediumType) && !isSelfManagement;
        const allowedOfficeCodes = (this.bdbSessionStrgService.get(SessionStorageKeysEnum.ALLOWED_CODES) || '').split(',');
        const isValidOfficeCode = allowedOfficeCodes.includes(this.bdbSessionStrgService.get(SessionStorageKeysEnum.OFFICE_CODE));
        const isRecognitionEnabled = this.bdbSessionStrgService.get(SessionStorageKeysEnum.RECOGNITION_ENABLED) === 'true';
        const isOfficeWithRF = isMediumTypeForRF && isValidOfficeCode && isRecognitionEnabled;
        const checkIdentityNumberLDAP = isOfficeWithRF ? true : sellerDocumentNumber === sellerIdentityNumberLdap;

        const controlShowModal = await this.runIdentityValidations(checkIdentityNumberLDAP);

        if (controlShowModal) {
            this.step = 1;
            this.setStepAndHandleError();
        }
    }

    private async runIdentityValidations(checkIdentityNumberLDAP: boolean) {
        if (!checkIdentityNumberLDAP && this.adviserService.isAuthAdviserEnabled()) {
            await this.reportAuthenticationRNEC(RNECEventEnum.RNEC, RNECEventEnum.ADVISER_FAIL, null, null, null);
            return true;
        }

        try {
            const validationResult = await this.authService.validateFingerprintsWithLocalService();

            logger.debug('Result from validate fingers with local service:', validationResult);

            const { result, hitStatus, customerInfo } = validationResult;

            if (result === true && hitStatus === 'HIT') {
                try {
                    this.bdbSessionStrgService.save(SessionStorageKeysEnum.LATITUDE, customerInfo.Latitud);
                    this.bdbSessionStrgService.save(SessionStorageKeysEnum.LONGITUDE, customerInfo.Longitud);

                    if (customerInfo.candidato) {
                        this.bdbSessionStrgService.save('candidate', JSON.stringify(customerInfo.candidato));
                    }

                    const responseFromReport = await this.reportAuthenticationRNEC(
                        RNECEventEnum.RNEC,
                        RNECEventEnum.OK,
                        null,
                        null,
                        customerInfo
                    );

                    this.validateReportAuthenticationRnec(responseFromReport);
                    return false;
                } catch (error) {
                    logger.error('error in validateFingerprintWithLocalService: ', error);
                    await this.reportAuthenticationRNEC(RNECEventEnum.RNEC, RNECEventEnum.ERROR, null, null, customerInfo);
                    return true;
                }
            }

            if (result === true && hitStatus === 'NOHIT') {
                await this.reportAuthenticationRNEC(RNECEventEnum.RNEC, RNECEventEnum.NO_HIT, null, null, customerInfo);
                // this.errorModal.openErrorModal(ErrorCode.NO_HIT);
                // return false;
                return true;
            }

            await this.reportAuthenticationRNEC(RNECEventEnum.RNEC, RNECEventEnum.ERROR, null, null, customerInfo);

            if (customerInfo.descripcionError.includes('Ha superado el numero de intentos por un dia')) {
                // this.redirectService.redirectToError(ErrorCode.ATTEMPTS_PER_DAY_EXCEEDED, 'v2');
                this.setErrorIntentoOlimpiaModalInfo()
                return false
            }

            return true;
        } catch (error) {
            await this.reportAuthenticationRNEC(RNECEventEnum.RNEC, RNECEventEnum.ERROR, null, null, null);
            return true;
        }
    }

    async captureFingerPrintWithRnec(finger: number, hand: string) {
        this.getGeolocationValues();

        const latitud = Number(this.bdbSessionStrgService.get(SessionStorageKeysEnum.LATITUDE));
        const longitud = Number(this.bdbSessionStrgService.get(SessionStorageKeysEnum.LONGITUDE));
        const site = this.bdbSessionStrgService.get(SessionStorageKeysEnum.SITE);
        const data = {
            'identificationType': 'C',
            'identificationNumber': this.bdbSessionStrgService.get(SessionStorageKeysEnum.IDENTITY_NUMBER),
            'deviceSerial': this.bdbSessionStrgService.get(SessionStorageKeysEnum.BIOMETRIC_SERIAL),
            'fingerNumber': finger,
            'hand': hand,
            'ip': this.bdbSessionStrgService.get(SessionStorageKeysEnum.PUBLIC_IP),
            'mediumType': this.bdbSessionStrgService.get(SessionStorageKeysEnum.MEDIUM_TYPE),
            'sellerIdentityType': this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELLER_IDENTITY_TYPE),
            'sellerDocumentNumber': this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELLER_IDENTITY_NUMBER),
            'sellerUsername': this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELLER_USER_NAME),
            'sellerIdentityNumberLdap': this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELLER_IDENTITY_NUMBER_LDAP),
            'latitud': latitud,
            'longitud': longitud,
            'uuidAuth': this.bdbSessionStrgService.get(SessionStorageKeysEnum.UUID_AUTH),
            'provider': 'Samsung',
            'product': ProductsEnum[site].name,
            'appName': ProductsEnum[site].apiName,
        };

        logger.group('Capture Finger Print Request:', data)

        const response = await this.captureFingerPrint(data)

        logger.log('Capture Finger Print Response:', response)
        logger.groupEnd()

        if (response.status === 200 && response.body.success) {
            finger === this.finger1.id ? this.setNextFingerprintLayout() : this.setValidationFingerprintLayout();
            return;
        }

        this.setStepAndHandleError(response.body.errorCode);
    }

    private async validateFingerprintWithRNEC(): Promise<void> {
        const uuid = UUID.UUID();
        const latitud = Number(this.bdbSessionStrgService.get(SessionStorageKeysEnum.LATITUDE));
        const longitud = Number(this.bdbSessionStrgService.get(SessionStorageKeysEnum.LONGITUDE));
        const site = this.bdbSessionStrgService.get(SessionStorageKeysEnum.SITE);

        const sellerDocumentNumber = this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELLER_IDENTITY_NUMBER);
        const sellerIdentityNumberLdap = this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELLER_IDENTITY_NUMBER_LDAP);
        const checkIdentityNumberLDAP = sellerDocumentNumber === sellerIdentityNumberLdap;

        const isAvailableToValidate = checkIdentityNumberLDAP || !this.adviserService.isAuthAdviserEnabled();

        if (!isAvailableToValidate) {
            await this.reportAuthenticationRNEC(RNECEventEnum.RNEC, RNECEventEnum.ADVISER_FAIL, null, null, null);
            this.step = 1;
            this.setStepAndHandleError();
            return;
        }

        const dataMobile = {
            'idTerminal': this.bdbSessionStrgService.get(SessionStorageKeysEnum.MAC),
            'ipTerminal': this.bdbSessionStrgService.get(SessionStorageKeysEnum.IP),
            'idUserMdm': this.bdbSessionStrgService.get(SessionStorageKeysEnum.USER),
            'idRequest': uuid,
            'identificationType': 'C',
            'identificationNumber': this.bdbSessionStrgService.get(SessionStorageKeysEnum.IDENTITY_NUMBER),
            'branchOffice': this.bdbSessionStrgService.get(SessionStorageKeysEnum.OFFICE_CODE),
            'expdate': this.bdbSessionStrgService.get(SessionStorageKeysEnum.EXPDATE),
            'mediumType': this.bdbSessionStrgService.get(SessionStorageKeysEnum.MEDIUM_TYPE),
            'sellerIdentityType': this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELLER_IDENTITY_TYPE),
            'sellerDocumentNumber': sellerDocumentNumber,
            'sellerUsername': this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELLER_USER_NAME),
            'sellerIdentityNumberLdap': sellerIdentityNumberLdap,
            'latitud': latitud,
            'longitud': longitud,
            'uuidAuth': this.bdbSessionStrgService.get(SessionStorageKeysEnum.UUID_AUTH),
            'provider': 'Samsung',
            'product': ProductsEnum[site].name,
            'appName': ProductsEnum[site].apiName
        };

        const response = await this.fingersMobileValidate(dataMobile);

        logger.debug('Response from validate fingers with RNEC:', response);

        if (response.status === 200 && response.body.accessToken) {
            this.saveInfoFromResponseAndRedirect(response.body.accessToken);
            return;
        }

        const errorCode = response.body.errorCode;

        // if (errorCode === ErrorCode.NO_HIT) {
        //     this.errorModal.openErrorModal(ErrorCode.NO_HIT);
        //     return;
        // }

        // if (errorCode === ErrorCode.ATTEMPTS_PER_DAY_EXCEEDED) {
        //     this.redirectService.redirectToError(ErrorCode.ATTEMPTS_PER_DAY_EXCEEDED, 'v2');
        //     return;
        // }

        const mediumType = this.bdbSessionStrgService.get(SessionStorageKeysEnum.MEDIUM_TYPE);
        const mustValidateExpDate = mediumType === '4' && response.status === 401;

        if (mustValidateExpDate) {
            this.checkExpDateTries();
            return;
        }

        this.step = 1;
        this.setStepAndHandleError(errorCode);
    }

    closeModal() {
        const captorStatus = this.bdbSessionStrgService.get('captorStatus');
        if (captorStatus && captorStatus === 'off') {
            this.callModalLoading();
            this.bdbSessionStrgService.delete('captorStatus');
            window.open(`olimpiait://reconoser?security='https'`);
            setTimeout(() => {
                this.loaderBdb.nativeElement.closeLoader();
                location.reload();
            }, 8000);
        } else {
            if (this.errorType === 1) {
                location.reload();
                return;
            }

            if (this.errorType === 2) {
                this.step = 2;
                this.capture();
            }

            if (this.intent === 3) {
                this.redirectService.redirectToError(ErrorCode.ATTEMPTS_EXCEEDED, 'v2');
            } else {
                this.capture();
            }

            this.intent++;
        }
    }

    async captureFingerPrint(data: any) {
        const url = `${environment.sandboxV3Domain}/rnec/V2/get-capture`
        return await this.authService.requestPostRencWithSandboxV3(data, url);
    }

    renderFingerprintCapture(finger: FingerModel, progressClass: any) {
        this.fingerNameClass = 'finger' + finger.id;
        this.fingerName = `${finger.name} ${finger.hand}`;
        this.progressClass = progressClass;
        this.capturing = true;
    }

    private setNextFingerprintLayout(): void {
        this.progressClass = 'progress-first-success';
        this.successfulCapturing('Siguiente huella ›');
        this.step = 3;
    }

    private setValidationFingerprintLayout(): void {
        this.progressClass = 'progress-second-success';
        this.successfulCapturing('Continuar ›');
        this.step = 4;
    }

    async fingersMobileValidate(data: any) {
        const url = `${environment.sandboxV3Domain}/rnec/V2/validate-mobile`
        return this.authService.requestPostRencWithSandboxV3(data, url);
    }

    validateReportAuthenticationRnec(responseFromReport): void {
        const mustValidateExpDate = ['4'].includes(this.bdbSessionStrgService.get('mediumType')) &&
            [401].includes(responseFromReport.status);
        if (responseFromReport.status === 200 && responseFromReport.body.accessToken) {
            this.saveInfoFromResponseAndRedirect(responseFromReport.body.accessToken);
        } else if (mustValidateExpDate) {
            this.checkExpDateTries();
        } else {
            this.reportAuthenticationRNEC(RNECEventEnum.RNEC, RNECEventEnum.ERROR, null, null, null);
        }
    }

    private async reportAuthenticationRNEC(eventName: string, eventResult: string, handIndex: string, finger: any, olimpiaResult: any) {
        try {
            return await this.authService.reportAuthenticationEventSandboxV3(eventName, eventResult, handIndex, finger, olimpiaResult);
        } catch (error) {
            logger.error('errorFromReport: ', error);
        }
    }

    private saveInfoFromResponseAndRedirect(accessToken: string) {
        this.bdbSessionStrgService.delete('expdatetries');
        this.bdbSessionStrgService.save(SessionStorageKeysEnum.ACCESS_TOKEN, accessToken);
        this.bdbSessionStrgService.save('path', '1');
        const mediumType = this.bdbSessionStrgService.get(SessionStorageKeysEnum.MEDIUM_TYPE);
        const forceMediumTypeValidation = this.mediumTypesOTP.includes(mediumType);
        const mediumTypesSecondFactor = environment.mediumTypesSecondFactor.split(',');
        const isMediumTypeForSecondFactor = mediumTypesSecondFactor.includes(mediumType);
        const customerState = this.bdbSessionStrgService.get(SessionStorageKeysEnum.CUSTOMER_STATE);
        const isClient = customerState === 'ACTIVE';
        const allowedSecondFactorConditions = this.bdbSessionStrgService.get(SessionStorageKeysEnum.SECOND_FACTOR);
        const actualConditionsSecondFactor = environment.useSecondFactor && isMediumTypeForSecondFactor && isClient;
        if (forceMediumTypeValidation) {
            this.sendOTP('SMS');
            this.router.navigate(['validate-one-time-password']);
        } else if (allowedSecondFactorConditions || actualConditionsSecondFactor) {
            this.router.navigate(['second-factor-selector']);
        } else {
            this.redirectService.redirectFromAuthentication();
        }
    }

    sendOTP(messageType: MessageType) {
        this.callModalLoading();
        const documentNumber = this.bdbSessionStrgService.get('identityNumber');
        const uuidAuth = this.bdbSessionStrgService.get('uuidAuth');
        const specificProductInfoUuid = this.bdbSessionStrgService.get(SessionStorageKeysEnum.SPECIFIC_PRODUCT_INFO_UUID);
        const cellphoneNumber = this.bdbSessionStrgService.get('cellphoneNumber');
        const otpRequest = {
            'numberIdentification': documentNumber,
            'keyPhone': cellphoneNumber,
            'numberIdentificationType': this.bdbSessionStrgService.get('identityType'),
            'cellphone': cellphoneNumber,
            'uuid': uuidAuth,
            'mode': messageType,
            'resendOtp': false,
            'specificProductInfoUuid': specificProductInfoUuid
        };
        this.oneTimePasswordService.sendOneTimePasswordOffice(otpRequest).subscribe(response => {
            this.loaderBdb.nativeElement.closeLoader();
            const status = response.status + '';
            const isStatusOk = status === SendOtpHttpStatusEnum.OTP_SENT_SUCESSFULLY ||
                status === SendOtpHttpStatusEnum.OTP_SENT_SUCESSFULLY_HOUSING_CLIENT;
            if (isStatusOk) {
                this.bdbSessionStrgService.delete('currentTimeInSeconds'); // reset timer of validate-otp
                this.router.navigate(['validate-one-time-password']);
            } else {
                this.oneTimePasswordService.redirectToSendOtpError(status);
            }
        }, error => {
            this.oneTimePasswordService.redirectToErrorScreen(ServiceErrorEnum.SEND_OTP_SERVICE_ERROR);
        });
    }

    public getIsAllowedForRecognition(customerStatesAllowed: CustomerStatesEnum[]): boolean {
        const customerState = this.bdbSessionStrgService.get(SessionStorageKeysEnum.CUSTOMER_STATE) as CustomerStatesEnum;
        const isAllowedCustomerStateRecognition = customerStatesAllowed.includes(customerState);

        const mediumType = this.bdbSessionStrgService.get(SessionStorageKeysEnum.MEDIUM_TYPE);
        const isSelfManagement = this.bdbSessionStrgService.get(SessionStorageKeysEnum.SELF_MANAGEMENT) + '' === 'true';
        const recognitionMediumTypes = this.bdbSessionStrgService.get(SessionStorageKeysEnum.RECOGNITION_MEDIUM_TYPES || '').split(',');
        const isMediumTypesForRecognition = recognitionMediumTypes.includes(mediumType) && !isSelfManagement;

        const allowedOfficeCodesRecognitionList = (this.bdbSessionStrgService.get(SessionStorageKeysEnum.ALLOWED_CODES) || '').split(',');
        const isValidOfficeCodeRecognition =
            allowedOfficeCodesRecognitionList.includes(this.bdbSessionStrgService.get(SessionStorageKeysEnum.OFFICE_CODE)) &&
            mediumType !== MediumTypeEnum.ALLIED.toString();

        const allowedAlliesRecognitionList = this.bdbSessionStrgService.get(SessionStorageKeysEnum.ALLOWED_ALLIES_RF || '').split(',');
        const isAllowedAlliedRecognition = allowedAlliesRecognitionList
            .includes(this.bdbSessionStrgService.get(SessionStorageKeysEnum.ALLIED_CODE));

        return isMediumTypesForRecognition && isAllowedCustomerStateRecognition &&
            (isValidOfficeCodeRecognition || isAllowedAlliedRecognition);
    }

    private setStepAndHandleError(errorCode?: string) {
        this.bdbSessionStrgService.delete('expdatetries');
        this.successAuth = false;

        switch (errorCode) {
            case ErrorCode.NO_HIT:
                this.redirectService.redirectToError(ErrorCode.BACKEND_ERROR, 'v2', false);
                break;
            case ErrorCode.ATTEMPTS_PER_DAY_EXCEEDED:
                this.redirectService.redirectToError(ErrorCode.ATTEMPTS_PER_DAY_EXCEEDED, 'v2');
                break;
            case ErrorCode.EXPDATE_MISMATCH:
                this.checkExpDateTries();
                break;
            case ErrorCode.CAPTURE_TIME_EXCEEDED:
                this.modalFunctionYes = this.closeModal;
                this.errorModal.openErrorModal(ErrorCode.CAPTURE_TIME_EXCEEDED)
                break;
            case ErrorCode.UNREGISTERED_CAPTOR:
                this.redirectService.redirectToError(ErrorCode.UNREGISTERED_CAPTOR, 'v2');
                break;
            default:
                this.setErrorModalInfo();
        }
    }

    private setErrorIntentoOlimpiaModalInfo() {
        this.errorType = 2;
        this.modalFunctionYes = this.closeModal;
        this.setModalTitles(
            'Has superado el número de intentos permitidos.',
            'Comunicate con la servilinea para mayor información (1-201)'
        );
        this.modalBdb.nativeElement.optionsButtons = [
            { id: '0', value: 'Continuar' }
        ];
        this.modalBdb.nativeElement.openAlert();
    }

    private setErrorAgenteModalInfo() {
        this.errorType = 2;
        this.modalFunctionYes = this.closeModal;
        this.setModalTitles(
            'Error en el Agente captor',
            'Comunicate con la servilinea para mayor información (1-202)'
        );
        this.modalBdb.nativeElement.optionsButtons = [
            { id: '0', value: 'Continuar' }
        ];
        this.modalBdb.nativeElement.openAlert();
    }

    private setErrorModalInfo() {
        this.modalFunctionYes = this.closeModal;
        this.setModalTitles(
            'Falló el servicio de autenticación',
            '¿Desea volver a intentar?'
        );
        this.modalBdb.nativeElement.optionsButtons = [
            { id: '0', value: 'Si' },
            { id: '1', value: 'No' }
        ];
        this.modalBdb.nativeElement.openAlert();
    }

    private setErrorAgentModalInfo() {
        this.modalFunctionYes = this.closeModal;
        this.setModalTitles(
            'Biométrico desactivado',
            'Activa el Biométrico para poder tomar la captura de las huellas'
        );
        this.modalBdb.nativeElement.optionsButtons = [
            { id: '0', value: 'Activar' }
        ];
        this.modalBdb.nativeElement.openAlert();
    }

    callModalLoading() {
        this.loaderBdb.nativeElement.openLoader();
    }

    setModalTitles(title: string, subtitle: string) {
        this.modalBdb.nativeElement.titleModal = title
        this.modalBdb.nativeElement.subTitle = subtitle
    }

    actionButtomModalBdb(evento: CustomEvent) {
        if (evento.detail.id === '0') {
            this.modalFunctionYes();
        } else {
            this.optionQuit();
        }
    }

    optionBack() {
        this.router.navigate([history.back()]);
    }

    optionQuit() {
        this.redirectService.quit();
    }

    private successfulCapturing(buttonText: string) {
        this.fingerNameClass = '';
        this.capturing = false;
        this.successFingerprint = true;
        this.buttonText = buttonText;
    }

    private checkExpDateTries(): void {
        let userTries = +this.bdbSessionStrgService.get('expdatetries') || 1;

        if (userTries >= this.EXPDATE_MAX_TRIES) {
            this.bdbSessionStrgService.delete('expdatetries');
            // this.redirectService.redirectToError(ErrorCode.EXPDATE_MISMATCH, 'v2');
            this.router.navigate(['/']);
        } else {
            userTries++;
            this.bdbSessionStrgService.save('expdatetries', `${userTries}`);
            this.router.navigate(['/expedition-date']);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/authentication-rnec/authentication-rnec.component.ts